import { AjaxRequest } from "rxjs/ajax";
import {basicRequestHeaders, RouteMethods} from "./RouteConfig";
import {Language} from "./LanguageConfig";

/**
 * @property protocol possible values http:// or https://
 * @property baseWeb string request api url of the backend
 * @property baseWebDomain string the domain for which the cookies are valid.
 * in most cases begins with a dot, to include all subdomains
 * @property devMode boolean sets the frontend into devMode to get more information on errors
 * @property serverMode string sets the server mode to single or multi in regards to the tenant system.
 * single does not allow for multi tenancy, multi allows for multi tenancy
 */
interface AppConfig {
    protocol: string
    baseWeb: string
    baseWebDomain: string
    devMode: boolean
    serverMode?: "single" | "multi"
}


interface AdvancedAppConfig extends AppConfig {
    /**
     *
     *
     * @param path the path to go to on the api backend, in get methods for list will most likely include params for userViewSettings
     * @param method the method to use to connect to the api backend
     * @see RouteMethods
     * @param body the object to be send to the api backend. mostly used in post and update RequestMethods
     * @param headers headers object to send with request
     */
    getRequestObject( path: string, method?: string, body?: any, headers?: Object ): AjaxRequest

}

declare global {
    interface Window {
        liveConfig: AppConfig
        selectedLanguage: Language
        token: string
    }
}
/**
 * Generates and returns the configuration settings required for the application's runtime environment.
 * This configuration adapts based on the current deployment environment (e.g., production, test, or local development).
 *
 * The function ensures the correct protocol, API base URL, and operational modes are applied dynamically
 * to support internal application logic and external API requests.
 *
 * @returns {AdvancedAppConfig} An object containing environmental settings and utility methods, including:
 * - `protocol`: The communication protocol (e.g., HTTP or HTTPS).
 * - `baseWeb`: The base URL for API requests, adjusted for the environment.
 * - `baseWebDomain`: The domain name used for the application in the current environment.
 * - `devMode`: A boolean flag indicating if the application is running in development mode.
 * - `serverMode`: The server architecture mode (e.g., "single" or "multi").
 * - `getRequestObject`: A utility function for constructing request objects based on specified parameters.
 *
 * @throws Will throw an error if required configuration properties on `window.liveConfig` are undefined or missing.
 */
const getConfig = (): AdvancedAppConfig => {
    //production and test server
    if( process.env.NODE_ENV === 'production' ) {
        const protocol = window.liveConfig.protocol;
        return {
            protocol: window.liveConfig.protocol,
            baseWeb: protocol + (( window.liveConfig.baseWeb.slice(-1) === "/" ) ? window.liveConfig.baseWeb : window.liveConfig.baseWeb+"/"),
            baseWebDomain: window.liveConfig.baseWebDomain,
            devMode: window.liveConfig.devMode,
            serverMode: window.liveConfig.serverMode,
            getRequestObject: ( path: string, method: string = RouteMethods.get, body?: any, headers: Object = basicRequestHeaders()  )  => {
                if( body ) {
                    return { url: path, method: method,  body: body, headers: headers, withCredentials: true, crossDomain: true, async: true, timeout: 120000, responseType: 'json', retryLimit: 3 };
                } else {
                    return { url: path, method: method, headers: headers, withCredentials: true, crossDomain: true,  async: true, timeout: 120000, responseType: 'json', retryLimit: 3  };
                }
            },
        };
    } else {
    
    // local
        return {
            protocol: window.liveConfig.protocol,
            baseWeb: window.liveConfig.protocol + 'gibcontrol.local/api/',
            baseWebDomain:'gibcontrol.local',
            devMode: true,
            serverMode: "multi",
            getRequestObject: ( path: string, method: string = "get", body?: any, headers: Object = basicRequestHeaders()  )  => {
                if( body ) {
                    return { url: path, withCredentials: true, method: method, body: body, headers: headers,  async: true, crossDomain: true, timeout: 120000, responseType: "json" };
                } else {
                    return { url: path, withCredentials: true, method: method, headers: headers,  async: true, timeout: 120000,crossDomain: true, responseType: "json" };
                }
            },
        };
    }
};

export const MAIN_APP_TITLE = "GibProject";

export const APP_CONFIG: AdvancedAppConfig = getConfig();

//create redux observable epic middleware